// frontend/src/pages/admin/AdminProductsPage.jsx
import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Edit, Trash2, Eye, Loader2, Search, Upload, Download,
  Filter, X, CheckSquare, Square, FileSpreadsheet, Plus,
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight
} from 'lucide-react';
import { PageSEO } from '../../components/SEO/PageSEO';
import api from '../../services/api';
import config from '../../config';

// تابع تبدیل متن به slug
const slugify = (text) => {
  if (!text) return '';
  return text
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-')      // فاصله → خط تیره
    .replace(/_/g, '-')         // زیرخط → خط تیره
    .replace(/[^\w\u0600-\u06FF\-]/g, '')  // حذف کاراکترهای غیرمجاز
    .replace(/-+/g, '-')        // خط تیره تکراری → یک خط تیره
    .replace(/^-|-$/g, '');     // حذف خط تیره از ابتدا و انتها
};

export const AdminProductsPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [pageSize, setPageSize] = useState(20);

  // State برای فیلترهای پیشرفته
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    brand: '',
    status: '',
    min_price: '',
    max_price: '',
    power_rating: '',
    seller: '',
  });
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [sellers, setSellers] = useState([]);

  // State برای انتخاب گروهی
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // State برای ایمپورت/اکسپورت
  const [showImportModal, setShowImportModal] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importSeller, setImportSeller] = useState('');
  const [exporting, setExporting] = useState(false);

  // State برای افزودن محصول جدید
  const [showAddModal, setShowAddModal] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newProduct, setNewProduct] = useState({
    name: '',
    slug: '',
    category: '',
    description: '',
    price: '',
    stock: '',
    status: 'active',
    warranty_months: 12,
    brand: '',
    power_rating: '',
    show_seller_name: true,
    show_price_on_call: false,
    contact_phone: '',
  });

  const fileInputRef = useRef(null);

  // گزینه‌های تعداد در هر صفحه
  const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

  useEffect(() => {
    fetchFiltersData();
    fetchProducts();
  }, [currentPage, pageSize, searchTerm, filters]);

  const fetchFiltersData = async () => {
    try {
      const [categoriesRes, filterOptionsRes] = await Promise.all([
        api.get('/products/categories/'),
        api.get('/products/products/filter-options/')
      ]);
      setCategories(categoriesRes.data.results || categoriesRes.data || []);
      setBrands(filterOptionsRes.data.brands || []);
      setSellers(filterOptionsRes.data.sellers || []);
    } catch (error) {
      console.error('Error fetching filter data:', error);
    }
  };

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        page_size: pageSize,
      };
      if (searchTerm) params.search = searchTerm;
      if (filters.category) params.category = filters.category;
      if (filters.brand) params.brand = filters.brand;
      if (filters.status) params.status = filters.status;
      if (filters.min_price) params.min_price = filters.min_price;
      if (filters.max_price) params.max_price = filters.max_price;
      if (filters.power_rating) params.power_rating = filters.power_rating;
      if (filters.seller) params.seller = filters.seller;

      const response = await api.get('/products/products/', { params });
      setProducts(response.data.results || []);
      setTotalCount(response.data.count || 0);
      setTotalPages(Math.ceil(response.data.count / pageSize));
      setSelectedProducts([]);
      setSelectAll(false);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteProduct = async (slug, name) => {
    if (window.confirm(`آیا از حذف محصول "${name}" اطمینان دارید؟`)) {
      try {
        await api.delete(`/products/products/${slug}/`);
        fetchProducts();
        alert('محصول با موفقیت حذف شد');
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('خطا در حذف محصول');
      }
    }
  };

  const deleteSelectedProducts = async () => {
    if (selectedProducts.length === 0) {
      alert('لطفاً حداقل یک محصول را انتخاب کنید');
      return;
    }

    if (window.confirm(`آیا از حذف ${selectedProducts.length} محصول انتخاب شده اطمینان دارید؟`)) {
      try {
        for (const slug of selectedProducts) {
          await api.delete(`/products/products/${slug}/`);
        }
        alert(`${selectedProducts.length} محصول با موفقیت حذف شد`);
        fetchProducts();
      } catch (error) {
        console.error('Error deleting products:', error);
        alert('خطا در حذف محصولات');
      }
    }
  };

  const handleSelectProduct = (slug) => {
    setSelectedProducts(prev =>
      prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.slug));
    }
    setSelectAll(!selectAll);
  };

  const resetFilters = () => {
    setFilters({
      category: '',
      brand: '',
      status: '',
      min_price: '',
      max_price: '',
      power_rating: '',
      seller: '',
    });
    setSearchTerm('');
    setCurrentPage(1);
  };

  // ✅ توابع مربوط به افزودن محصول جدید
  const handleAddInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (type === 'checkbox') {
      setNewProduct(prev => ({ ...prev, [name]: checked }));
    } else if (name === 'name') {
      // وقتی نام تغییر می‌کند، slug خودکار ساخته شود
      const generatedSlug = slugify(value);
      setNewProduct(prev => ({
        ...prev,
        name: value,
        slug: generatedSlug
      }));
    } else if (name === 'slug') {
      // کاربر دستی slug را وارد می‌کند - بدون تغییر فوری
      setNewProduct(prev => ({ ...prev, slug: value }));
    } else {
      setNewProduct(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleAddProduct = async () => {
    // اعتبارسنجی
    if (!newProduct.name || !newProduct.category || !newProduct.price || !newProduct.stock) {
      alert('لطفاً همه فیلدهای ضروری را پر کنید');
      return;
    }

    // پاکسازی slug قبل از ارسال
    let finalSlug = newProduct.slug;
    if (finalSlug) {
      finalSlug = slugify(finalSlug);
    }
    if (!finalSlug) {
      finalSlug = slugify(newProduct.name);
    }

    const accessToken = localStorage.getItem('access_token');
    setAdding(true);

    try {
      const productData = {
        name: newProduct.name,
        slug: finalSlug,
        category: parseInt(newProduct.category),
        description: newProduct.description,
        price: parseInt(newProduct.price),
        stock: parseInt(newProduct.stock),
        status: newProduct.status,
        warranty_months: parseInt(newProduct.warranty_months),
        brand: newProduct.brand || null,
        power_rating: newProduct.power_rating ? parseFloat(newProduct.power_rating) : null,
        show_seller_name: newProduct.show_seller_name,
        specifications: {},
        show_price_on_call: newProduct.show_price_on_call,
        contact_phone: newProduct.contact_phone || null,
      };

      const response = await fetch(config.getApiUrl('/products/products/'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`
        },
        body: JSON.stringify(productData)
      });

      if (response.ok) {
        alert('محصول با موفقیت اضافه شد');
        setShowAddModal(false);
        setNewProduct({
          name: '',
          slug: '',
          category: '',
          description: '',
          price: '',
          stock: '',
          status: 'active',
          warranty_months: 12,
          brand: '',
          power_rating: '',
          show_seller_name: true,
        });
        fetchProducts();
      } else {
        const error = await response.json();
        alert('خطا: ' + JSON.stringify(error));
      }
    } catch (error) {
      console.error('Error adding product:', error);
      alert('خطا در ارتباط با سرور');
    } finally {
      setAdding(false);
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      alert('لطفاً فایل اکسل را انتخاب کنید');
      return;
    }
    if (!importSeller) {
      alert('لطفاً فروشنده را انتخاب کنید');
      return;
    }

    const formData = new FormData();
    formData.append('excel_file', importFile);
    formData.append('seller', importSeller);

    setImporting(true);
    try {
      const accessToken = localStorage.getItem('access_token');
      const response = await fetch(config.getApiUrl('/products/products/import-excel/'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`
        },
        body: formData
      });

      if (response.ok) {
        alert('محصولات با موفقیت ایمپورت شدند');
        setShowImportModal(false);
        setImportFile(null);
        setImportSeller('');
        fetchProducts();
      } else {
        const error = await response.json();
        alert('خطا: ' + JSON.stringify(error));
      }
    } catch (error) {
      console.error('Error importing products:', error);
      alert('خطا در ایمپورت محصولات');
    } finally {
      setImporting(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (filters.category) params.append('category', filters.category);
      if (filters.brand) params.append('brand', filters.brand);
      if (filters.status) params.append('status', filters.status);
      if (filters.min_price) params.append('min_price', filters.min_price);
      if (filters.max_price) params.append('max_price', filters.max_price);
      if (filters.power_rating) params.append('power_rating', filters.power_rating);
      if (filters.seller) params.append('seller', filters.seller);

      const url = config.getApiUrl(`/products/products/export-excel/?${params.toString()}`);
      console.log('Exporting from:', url);

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('access_token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `products_export_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.xlsx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        alert('محصولات با موفقیت استخراج شدند');
      } else {
        const error = await response.json();
        alert('خطا: ' + JSON.stringify(error));
      }
    } catch (error) {
      console.error('Error exporting products:', error);
      alert('خطا در استخراج محصولات');
    } finally {
      setExporting(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  if (loading && products.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <>
      <PageSEO title="مدیریت محصولات | پنل ادمین" noindex={true} />

      <div>
        {/* هدر و دکمه‌ها */}
        <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">مدیریت محصولات</h1>
            <p className="text-sm text-gray-500 mt-1">مدیریت، ایمپورت و اکسپورت محصولات</p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            >
              <Plus className="w-4 h-4" />
              افزودن محصول جدید
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
            >
              <Upload className="w-4 h-4" />
              ایمپورت از اکسل
            </button>
            <button
              onClick={handleExport}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition disabled:opacity-50"
            >
              {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              استخراج به اکسل
            </button>
            {selectedProducts.length > 0 && (
              <button
                onClick={deleteSelectedProducts}
                className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition"
              >
                <Trash2 className="w-4 h-4" />
                حذف گروهی ({selectedProducts.length})
              </button>
            )}
          </div>
        </div>

        {/* نوار جستجو و فیلتر */}
        <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="جستجوی محصول (نام، برند، کد)..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-10 pl-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition ${showAdvancedFilters ? 'bg-blue-50 border-blue-500 text-blue-600' : 'hover:bg-gray-50'
                }`}
            >
              <Filter className="w-4 h-4" />
              فیلتر پیشرفته
              {Object.values(filters).some(f => f) && (
                <span className="bg-blue-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  !
                </span>
              )}
            </button>

            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="px-4 py-2 border rounded-lg"
            >
              {PAGE_SIZE_OPTIONS.map(size => (
                <option key={size} value={size}>{size} عدد در صفحه</option>
              ))}
            </select>
          </div>

          {/* فیلترهای پیشرفته */}
          {showAdvancedFilters && (
            <div className="mt-4 pt-4 border-t grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">دسته‌بندی</label>
                <select
                  value={filters.category}
                  onChange={(e) => setFilters(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">همه دسته‌بندی‌ها</option>
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.slug}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">برند</label>
                <select
                  value={filters.brand}
                  onChange={(e) => setFilters(prev => ({ ...prev, brand: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">همه برندها</option>
                  {brands.map(brand => (
                    <option key={brand} value={brand}>{brand}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">وضعیت</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">همه</option>
                  <option value="active">فعال</option>
                  <option value="inactive">غیرفعال</option>
                  <option value="out_of_stock">ناموجود</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">توان (کیلووات)</label>
                <input
                  type="number"
                  step="0.1"
                  value={filters.power_rating}
                  onChange={(e) => setFilters(prev => ({ ...prev, power_rating: e.target.value }))}
                  placeholder="مثال: 5.5"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">حداقل قیمت</label>
                <input
                  type="number"
                  value={filters.min_price}
                  onChange={(e) => setFilters(prev => ({ ...prev, min_price: e.target.value }))}
                  placeholder="تومان"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">حداکثر قیمت</label>
                <input
                  type="number"
                  value={filters.max_price}
                  onChange={(e) => setFilters(prev => ({ ...prev, max_price: e.target.value }))}
                  placeholder="تومان"
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">فروشنده</label>
                <select
                  value={filters.seller}
                  onChange={(e) => setFilters(prev => ({ ...prev, seller: e.target.value }))}
                  className="w-full border rounded-lg px-3 py-2"
                >
                  <option value="">همه فروشندگان</option>
                  {sellers.map(seller => (
                    <option key={seller.id} value={seller.id}>{seller.username}</option>
                  ))}
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={resetFilters}
                  className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
                >
                  <X className="w-4 h-4" />
                  حذف همه فیلترها
                </button>
              </div>
            </div>
          )}
        </div>

        {/* انتخاب گروهی */}
        <div className="mb-4 flex justify-between items-center">
          <button
            onClick={handleSelectAll}
            className="flex items-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition"
          >
            {selectAll ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
            {selectAll ? 'لغو انتخاب همه' : 'انتخاب همه'}
          </button>
          <div className="text-sm text-gray-500">
            نمایش {startItem} تا {endItem} از {totalCount} محصول
          </div>
        </div>

        {/* جدول محصولات */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase w-10">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="w-4 h-4 rounded"
                    />
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">تصویر</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">نام محصول</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">دسته‌بندی</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">فروشنده</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">قیمت</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">موجودی</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">وضعیت</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">عملیات</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-12 text-center text-gray-500">
                      محصولی یافت نشد
                    </td>
                  </tr>
                ) : (
                  products.map((product) => (
                    <tr key={product.id} className="hover:bg-gray-50 transition">
                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedProducts.includes(product.slug)}
                          onChange={() => handleSelectProduct(product.slug)}
                          className="w-4 h-4 rounded"
                        />
                      </td>
                      <td className="px-4 py-3">
                        <img
                          src={product.main_image || '/placeholder-product.jpg'}
                          className="w-10 h-10 rounded object-cover"
                          alt={product.name}
                        />
                      </td>
                      <td className="px-4 py-3 font-medium max-w-xs truncate">{product.name}</td>
                      <td className="px-4 py-3">{product.category_name || '-'}</td>
                      <td className="px-4 py-3">{product.seller_name_display || product.seller_id || '-'}</td>
                      <td className="px-4 py-3">{product.price?.toLocaleString()} تومان</td>
                      <td className="px-4 py-3">{product.stock}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 text-xs rounded-full ${product.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {product.status === 'active' ? 'فعال' : 'غیرفعال'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <Link to={`/products/${product.slug}`} className="text-green-600 hover:text-green-800">
                            <Eye className="w-4 h-4" />
                          </Link>
                          <Link to={`/products/${product.slug}/edit`} className="text-blue-600 hover:text-blue-800">
                            <Edit className="w-4 h-4" />
                          </Link>
                          <button onClick={() => deleteProduct(product.slug, product.name)} className="text-red-600 hover:text-red-800">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* صفحه‌بندی */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center gap-2 mt-6 flex-wrap">
            <button
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              className="p-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
            >
              <ChevronsRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="p-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
            >
              <ChevronRight className="w-5 h-5" />
            </button>

            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => handlePageChange(pageNum)}
                    className={`w-10 h-10 rounded-lg transition ${currentPage === pageNum
                      ? 'bg-blue-600 text-white'
                      : 'border hover:bg-gray-50'
                      }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="p-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              className="p-2 border rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
            >
              <ChevronsLeft className="w-5 h-5" />
            </button>
          </div>
        )}
      </div>

      {/* مودال افزودن محصول جدید */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center p-6 border-b border-gray-100 sticky top-0 bg-white">
              <div className="flex items-center gap-2">
                <Plus className="w-5 h-5 text-indigo-600" />
                <h2 className="text-xl font-bold text-gray-900">افزودن محصول جدید</h2>
              </div>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">نام محصول *</label>
                  <input
                    type="text"
                    name="name"
                    value={newProduct.name}
                    onChange={handleAddInputChange}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slug (آدرس یکتا)
                    <span className="text-xs text-gray-500 mr-2">(اختیاری - در صورت خالی بودن از نام ساخته می‌شود)</span>
                  </label>
                  <input
                    type="text"
                    name="slug"
                    value={newProduct.slug}
                    onChange={handleAddInputChange}
                    className="w-full border rounded-lg px-3 py-2 font-mono text-sm focus:ring-2 focus:ring-blue-500"
                    placeholder="مثال: محصول-جدید-123"
                  />
                  <small className="text-xs text-gray-500 block mt-1">
                    آدرس نهایی: {config.SITE_URL}/products/{newProduct.slug || '...'}
                  </small>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">دسته‌بندی *</label>
                  <select
                    name="category"
                    value={newProduct.category}
                    onChange={handleAddInputChange}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">انتخاب کنید</option>
                    {categories.map((cat) => (
                      <option key={cat.id} value={cat.id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">وضعیت</label>
                  <select
                    name="status"
                    value={newProduct.status}
                    onChange={handleAddInputChange}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="active">فعال</option>
                    <option value="inactive">غیرفعال</option>
                    <option value="out_of_stock">ناموجود</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">قیمت (تومان) *</label>
                  <input
                    type="number"
                    name="price"
                    value={newProduct.price}
                    onChange={handleAddInputChange}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                {/* ========== بخش تماس برای قیمت ========== */}
                <div className="col-span-2 border-t pt-4 mt-2">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">تنظیمات تماس برای قیمت</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        name="show_price_on_call"
                        checked={newProduct.show_price_on_call}
                        onChange={handleAddInputChange}
                        className="w-4 h-4 text-blue-600 rounded"
                      />
                      <div>
                        <label className="text-sm font-medium text-gray-700 cursor-pointer">
                          نمایش "تماس برای قیمت"
                        </label>
                        <p className="text-xs text-gray-500">در صورت فعال بودن، قیمت نمایش داده نمی‌شود و دکمه تماس نشان داده می‌شود</p>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">شماره تماس اختصاصی</label>
                      <input
                        type="text"
                        name="contact_phone"
                        value={newProduct.contact_phone}
                        onChange={handleAddInputChange}
                        className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                        placeholder="مثال: 09123456789"
                        disabled={!newProduct.show_price_on_call}
                      />
                      <p className="text-xs text-gray-400 mt-1">در صورت خالی بودن، شماره اصلی سایت نمایش داده می‌شود</p>
                    </div>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">موجودی *</label>
                  <input
                    type="number"
                    name="stock"
                    value={newProduct.stock}
                    onChange={handleAddInputChange}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">گارانتی (ماه)</label>
                  <input
                    type="number"
                    name="warranty_months"
                    value={newProduct.warranty_months}
                    onChange={handleAddInputChange}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">برند</label>
                  <input
                    type="text"
                    name="brand"
                    value={newProduct.brand}
                    onChange={handleAddInputChange}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    placeholder="مثال: JA Solar"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">توان (کیلووات)</label>
                  <input
                    type="number"
                    step="0.1"
                    name="power_rating"
                    value={newProduct.power_rating}
                    onChange={handleAddInputChange}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                    placeholder="مثال: 5.5"
                  />
                </div>
                <div className="col-span-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="show_seller_name"
                      checked={newProduct.show_seller_name}
                      onChange={handleAddInputChange}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">نمایش نام فروشنده در صفحه محصول</span>
                  </label>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">توضیحات *</label>
                <textarea
                  name="description"
                  value={newProduct.description}
                  onChange={handleAddInputChange}
                  rows="4"
                  className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
                >
                  انصراف
                </button>
                <button
                  onClick={handleAddProduct}
                  disabled={adding}
                  className="flex items-center gap-2 px-6 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition disabled:opacity-50"
                >
                  {adding ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                  {adding ? 'در حال افزودن...' : 'افزودن محصول'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* مودال ایمپورت اکسل */}
      {showImportModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex justify-between items-center p-6 border-b border-gray-100">
              <div className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-green-600" />
                <h2 className="text-xl font-bold text-gray-900">ایمپورت محصولات از اکسل</h2>
              </div>
              <button
                onClick={() => setShowImportModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4">
              <div className="bg-blue-50 rounded-lg p-4 text-sm text-blue-800">
                <p className="font-medium mb-2">📋 راهنمای فرمت فایل اکسل</p>
                <ul className="space-y-1 text-xs">
                  <li><strong>name</strong> - نام محصول (اجباری)</li>
                  <li><strong>category</strong> - نام دسته‌بندی (اجباری)</li>
                  <li><strong>price</strong> - قیمت (اجباری)</li>
                  <li><strong>brand</strong> - برند محصول</li>
                  <li><strong>stock</strong> - موجودی (پیش‌فرض: 10)</li>
                </ul>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">انتخاب فایل اکسل</label>
                <input
                  type="file"
                  ref={fileInputRef}
                  accept=".xlsx, .xls"
                  onChange={(e) => setImportFile(e.target.files[0])}
                  className="w-full border rounded-lg px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">فروشنده</label>
                <select
                  value={importSeller}
                  onChange={(e) => setImportSeller(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2"
                  required
                >
                  <option value="">انتخاب فروشنده</option>
                  {sellers.map(seller => (
                    <option key={seller.id} value={seller.id}>{seller.username}</option>
                  ))}
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  onClick={() => setShowImportModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50 transition"
                >
                  انصراف
                </button>
                <button
                  onClick={handleImport}
                  disabled={importing}
                  className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition disabled:opacity-50"
                >
                  {importing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {importing ? 'در حال ایمپورت...' : 'ایمپورت'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
